import { useState } from "react";

/** 달력. **정본은 좁은 쪽(`A-45b`)이고 이 화면은 그것을 담기만 한다** — 같은 것을 두 번
 *  설계하면 둘이 갈라진다. 담기는 그릇만 시트에서 드롭다운으로 바뀐다(PRD 7.7.6b).
 *
 *  값은 로컬 날짜 `YYYY-MM-DD` 다. 시각으로 자르는 일은 서버로 보내기 직전 한 곳
 *  (`resolveDateRange`)에서만 한다 — 여기서 `23:59:59` 를 붙이면 규약이 둘이 된다. */

const DOW = ["일", "월", "화", "수", "목", "금", "토"] as const;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export function toIsoDate(year: number, month: number, day: number): string {
  return `${year}-${pad2(month + 1)}-${pad2(day)}`;
}

function parseIsoDate(value: string | undefined): Date | null {
  if (!value) return null;
  const parsed = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(parsed) ? null : new Date(parsed);
}

interface Cursor {
  year: number;
  /** 0-based. `Date.UTC` 와 같은 규약이다. */
  month: number;
}

/** 처음 펼칠 달.
 *
 * **오늘은 셀러의 로컬 날짜로 읽는다.** `getUTCMonth()` 로 읽으면 KST 9월 1일 오전 8시에
 * 열었을 때 `2026년 8월` 이 펼쳐진다(UTC 로는 아직 8월 31일이라서). 앱의 달력은 로컬
 * 기준이라 같은 시각에 두 화면이 다른 달을 연다. 기간 경계를 로컬 자정으로 자르는
 * `resolveDateRange` 와도 이쪽이 맞다.
 *
 * 이미 고른 날(`from`)이 있으면 그 달을 연다. 그 값은 날짜만 있는 문자열이라 UTC 자정으로
 * 읽고, 연·월도 같은 규약으로 꺼낸다. */
function openingMonth(from: string | undefined): Cursor {
  const picked = parseIsoDate(from);
  if (picked) return { year: picked.getUTCFullYear(), month: picked.getUTCMonth() };
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() };
}

export interface CalendarProps {
  from: string | undefined;
  to: string | undefined;
  onChange: (next: { from: string | undefined; to: string | undefined }) => void;
}

export function Calendar({ from, to, onChange }: CalendarProps) {
  const [cursor, setCursor] = useState<Cursor>(() => openingMonth(from));

  const first = new Date(Date.UTC(cursor.year, cursor.month, 1));
  const leading = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(cursor.year, cursor.month + 1, 0)).getUTCDate();
  const cells: Array<{ key: string; label: number; iso: string | null }> = [];

  for (let i = leading; i > 0; i -= 1) {
    const d = new Date(Date.UTC(cursor.year, cursor.month, 1 - i));
    cells.push({ key: `lead-${i}`, label: d.getUTCDate(), iso: null });
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push({ key: `d-${d}`, label: d, iso: toIsoDate(cursor.year, cursor.month, d) });
  }
  while (cells.length % 7 !== 0) {
    const overflow = cells.length - leading - daysInMonth + 1;
    cells.push({ key: `tail-${overflow}`, label: overflow, iso: null });
  }

  /** 격자는 **줄로 나뉘어야 격자로 읽힌다**. `role="grid"` 안에 칸만 늘어놓으면 스크린리더가
   *  줄과 열을 말할 수 없어 날짜를 훑을 수 없다. 줄은 `display: contents`(`.calrow`) 라
   *  일곱 칸 격자 배치는 그대로다. */
  const weeks: Array<typeof cells> = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  function pick(iso: string) {
    // 시작만 있거나 둘 다 있으면 새로 시작한다. 하나만 있으면 그 사이가 구간이다.
    if (!from || (from && to)) {
      onChange({ from: iso, to: undefined });
      return;
    }
    if (iso < from) onChange({ from: iso, to: from });
    else onChange({ from, to: iso });
  }

  function shift(delta: number) {
    const next = new Date(Date.UTC(cursor.year, cursor.month + delta, 1));
    setCursor({ year: next.getUTCFullYear(), month: next.getUTCMonth() });
  }

  return (
    <>
      <div className="calhead">
        <button type="button" className="nav" aria-label="이전 달" onClick={() => shift(-1)}>
          ‹
        </button>
        <span>
          {cursor.year}년 {cursor.month + 1}월
        </span>
        <button type="button" className="nav" aria-label="다음 달" onClick={() => shift(1)}>
          ›
        </button>
      </div>
      <div className="cal" role="grid" aria-label={`${cursor.year}년 ${cursor.month + 1}월`}>
        <span className="calrow" role="row">
          {DOW.map((d) => (
            <span className="dow" role="columnheader" key={d}>
              {d}
            </span>
          ))}
        </span>
        {weeks.map((week, w) => (
          <span className="calrow" role="row" key={`w-${week[0]?.key ?? w}`}>
            {week.map((cell) => {
              if (!cell.iso) {
                // 옆 달의 날. 고를 수 없지만 칸은 남긴다 — 빼면 그 줄이 일곱 칸이 아니게 된다.
                return (
                  <span className="d off" role="gridcell" aria-disabled="true" key={cell.key}>
                    {cell.label}
                  </span>
                );
              }
              const iso = cell.iso;
              const isStart = from === iso;
              const isEnd = to === iso;
              const inRange = Boolean(from && to && iso > from && iso < to);
              const cls = [
                "d",
                isStart || isEnd ? "sel" : "",
                isStart ? "s" : "",
                isEnd ? "e" : "",
                inRange ? "range" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  type="button"
                  className={cls}
                  role="gridcell"
                  key={cell.key}
                  // 격자에서는 고른 칸을 `selected` 로 말한다. `pressed` 는 버튼의 말이다.
                  aria-selected={isStart || isEnd || inRange}
                  aria-label={iso}
                  onClick={() => pick(iso)}
                >
                  {cell.label}
                </button>
              );
            })}
          </span>
        ))}
      </div>
    </>
  );
}
